import type { PropsWithChildren } from 'react';

import type { IRemoteDeclaration } from '~/modules/remotes/types.ts';

import { RemotesContext } from '../contexts/RemotesContext';
import { remotes } from '../registry';

type RemoteOverride = Pick<IRemoteDeclaration, 'name' | 'root'> &
  Partial<Pick<IRemoteDeclaration, 'status'>>;

interface RemotesProviderProps extends PropsWithChildren {
  overrides?: RemoteOverride[];
}

function getRemotes(
  defaultRemotes: IRemoteDeclaration[],
  overrides: RemoteOverride[] = [],
) {
  if (overrides.length === 0) {
    return defaultRemotes;
  }

  const overrideMap = new Map<string, NonNullable<typeof overrides>[number]>(
    overrides.map((override) => [override.name, override]),
  );

  return defaultRemotes.map((remote) => {
    const override = overrideMap.get(remote.name);

    if (!override) {
      return remote;
    }

    return {
      ...remote,
      root: override.root,
      ...(override.status && { status: override.status }),
    };
  });
}

export function RemotesProvider({ children, overrides }: RemotesProviderProps) {
  return (
    <RemotesContext.Provider
      value={{
        remotes: getRemotes(remotes, overrides),
      }}
    >
      {children}
    </RemotesContext.Provider>
  );
}

RemotesProvider.displayName = 'RemotesProvider';
